// Copyright (c) Docugami, Inc. All rights reserved.

import { useEffect, useRef, useState } from "react";
import Measure from "react-measure";
import styled from "styled-components";

const PanelRoot = styled.div<{ isOpen?: boolean, scrollable?: boolean, maxHeight: number, panelHeight: number, duration: number }>`
    width: 100%;
    overflow: ${props => !!props.scrollable ? 'auto' : 'hidden'};
    >.contentPanel {
        height: ${props => props.panelHeight}px;
        max-height: ${props => !!props.isOpen ? `${props.maxHeight}px` : '0px'};
        transition: max-height ${props => props.duration}s ease-in-out;
    }
`

interface SlidingPanelProps {
    maxHeight?: number;
    isOpen: boolean;
    durationS?: number;
    children?: JSX.Element | JSX.Element[];
    scrollable?: boolean;
    onAnimationComplete?: (isOpen: boolean) => void;
}

export const SlidingPanel = (props: SlidingPanelProps): JSX.Element => {
    const [panelHeight, setPanelHeight] = useState(0);
    const [targetHeight, setTargetHeight] = useState(0);
    const [animating, setAnimating] = useState(false);
    const lifetimeRef = useRef(true);
    const durationRef = useRef(props.durationS ?? .35);
    const animationCompleteCallbackRef = useRef(props.onAnimationComplete);

    useEffect(() => {
        return () => { lifetimeRef.current = false; }
    }, []);

    useEffect(() => {
        animationCompleteCallbackRef.current = props.onAnimationComplete;
    }, [props.onAnimationComplete])

    useEffect(() => {
        durationRef.current = props.durationS ?? .35;
    }, [props.durationS]);

    useEffect(() => {
        if (!animating) {
            setPanelHeight(targetHeight);
        }
        // eslint-disable-next-line
    }, [animating]);

    useEffect(() => {
        setAnimating(true);
        const isOpen = props.isOpen;
        const token = setTimeout(() => {
            if (lifetimeRef.current) {
                setAnimating(false);
                animationCompleteCallbackRef.current?.(isOpen);
            }
        }, durationRef.current * 1000);

        return () => clearTimeout(token);
    }, [props.isOpen]);

    return (
        <PanelRoot
            isOpen={props.isOpen}
            scrollable={!!props.scrollable && !animating}
            maxHeight={Math.min(props.maxHeight ?? Infinity, targetHeight)}
            panelHeight={panelHeight}
            duration={props.durationS !== undefined ? props.durationS : .35}>
            <div className="contentPanel">
                <Measure
                    bounds
                    onResize={contentRect => {
                        const newHeight = contentRect.bounds!.bottom - contentRect.bounds!.top;
                        if (newHeight > panelHeight) {
                            setPanelHeight(newHeight);
                        }

                        setTargetHeight(newHeight);
                    }}>
                    {({ measureRef }) => (
                        <div ref={measureRef}>
                            {props.children}
                        </div>
                    )}
                </Measure>
            </div>
        </PanelRoot>
    )
};
